import { Users, TrendingUp, Database, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export default function NetworkEffect() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="technology" className="py-32 bg-transparent relative overflow-hidden">

      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-20 w-96 h-96 bg-emerald-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500/10 rounded-full mb-6 border border-emerald-500/20">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-medium text-emerald-400">The Network Effect</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Every Interview Makes Us <br />
            <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
              10X Smarter
            </span>
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            With each interview, our AI learns behavioral patterns, communication styles, and role-specific competencies. The more candidates we assess, the more accurate we become.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              icon: Database,
              title: '5000+ Interviews',
              desc: 'Behavioral data points growing exponentially',
              delay: '0ms',
              stat: '5000+'
            },
            {
              icon: Users,
              title: 'Cross-Company Insights',
              desc: 'Learn from hiring patterns across industries',
              delay: '200ms',
              stat: '500+'
            },
            {
              icon: TrendingUp,
              title: 'Self-Improving AI',
              desc: 'Accuracy increases with every assessment - with a high NPS metric',
              delay: '400ms',
              stat: '98.4%'
            }
          ].map((item, index) => (
            <div
              key={index}
              className={`glass-dark p-8 rounded-3xl border border-emerald-500/20 hover:border-emerald-500/50 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: item.delay }}
            >
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-2xl flex items-center justify-center mb-6">
                <item.icon className="w-8 h-8 text-emerald-400" />
              </div>
              <div className="text-4xl font-bold text-white mb-2">{item.stat}</div>
              <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
              <p className="text-slate-400 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className={`mt-16 text-center transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="glass-dark inline-block px-8 py-6 rounded-3xl border border-emerald-500/30">
            <p className="text-white font-semibold text-lg mb-2">
              The more you use GreenTick, the better it gets for everyone.
            </p>
            <p className="text-slate-400 text-sm">
              A compounding advantage no competitor can replicate.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
